import PropTypes from "prop-types";
import { useCallback, useState } from "react";
import { Link as RouterLink, useHistory } from "react-router-dom";

import useClientRect from "hooks/use-client-rect";
import { usePostJson } from "hooks/use-fetch";
import safeRedirect from "utils/safe-redirect";
import { transformErrors } from "utils/transform-errors";

import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import headerLogoPath from "images/logos/logotype-dark.svg";

import ValidationErrors from "screens/shared/validation-errors";

import {
  Box,
  Button,
  Container,
  Divider,
  Link,
  Stack,
  TextField,
  Typography,
} from "components/core";
import GoogleButtonGSI from "components/google-button-gsi";
import Turnstile, { useTurnstile } from "components/turnstile/turnstile";

const AlternativeLoginOptions = ({ setErrors }) => {
  const [buttonRect, buttonRef] = useClientRect();
  const [authenticateSso, ssoLoading] = usePostJson();

  const handleIdTokenAndPayload = useCallback(
    async (token, googleUser) => {
      const result = await authenticateSso("/dashboard/sso_authenticate", {
        email: googleUser.email,
        token,
      });

      if (result.errors) {
        if (result.errors.user) {
          window.location.assign("/dashboard/login/link-sso");
        } else {
          setErrors([result.errors.email]);
        }
      } else {
        safeRedirect(result.returnPath);
      }
    },
    [setErrors, authenticateSso],
  );

  return (
    <Stack spacing={2}>
      <GoogleButtonGSI
        buttonOptions={{
          text: "continue_with",
          logo_alignment: "center",
        }}
        disabled={ssoLoading}
        initializeProps={{ context: "signin" }}
        onIdTokenAndPayload={handleIdTokenAndPayload}
        {...(buttonRect && { width: Math.floor(buttonRect.width) })}
      />
      <Button
        ref={buttonRef}
        component={RouterLink}
        startIcon={<MailOutlineRoundedIcon />}
        sx={{
          color: "#3c4043",
          borderColor: "#dadce0",
          fontWeight: "regular",
          fontSize: "14px",
          fontFamily: "Google Sans,arial,sans-serif",
          letterSpacing: "0.25px",
        }}
        to="/email"
        variant="outlined"
      >
        Continue with email
      </Button>
    </Stack>
  );
};

AlternativeLoginOptions.propTypes = {
  setErrors: PropTypes.func.isRequired,
};

const EnterNumber = ({
  mobileNumber,
  onMobileNumberChange,
  onSuccess,
  initialErrors,
  turnstileEnabled,
}) => {
  const [errors, setErrors] = useState(initialErrors || {});
  const [login, loginLoading] = usePostJson();
  const history = useHistory();

  const { turnstileToken, turnstileVisible, turnstileHandlers } = useTurnstile({
    onError: () =>
      setErrors([
        "Sorry, it looks like there's a problem with our service right now. Please try again later or let us know if the problem continues.",
      ]),
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (turnstileVisible && !turnstileToken) return;

    const response = await login("/account/api/phone_logins", {
      mobileNumber,
      turnstileToken,
    });

    if (response.errors) {
      setErrors(transformErrors(response.errors));
    } else {
      onSuccess(response);

      if (response.verified) {
        history.push("/check");
      } else {
        history.push("/verify");
      }
    }
  };

  return (
    <Container maxWidth="xs" sx={{ pb: [2, 3], pt: [3, 4] }}>
      <Stack spacing={4}>
        <Box
          alignSelf="flex-start"
          alt="Koru Kids Logo"
          component="img"
          height="48px"
          src={headerLogoPath}
        />
        <Stack component="form" spacing={2} onSubmit={handleSubmit}>
          <ValidationErrors errors={errors} />
          <Typography variant="title">Log in</Typography>
          <TextField
            error={!!errors.mobileNumber}
            helperText={errors.mobileNumber}
            id="mobile_number"
            label="Mobile number"
            type="tel"
            value={mobileNumber}
            onChange={onMobileNumberChange}
          />
          <Turnstile
            action="login"
            enabled={turnstileEnabled}
            {...turnstileHandlers}
            visible={turnstileVisible}
          />
          <Button
            disabled={turnstileVisible && !turnstileToken}
            loading={(!turnstileVisible && !turnstileToken) || loginLoading}
            type="submit"
          >
            Continue
          </Button>
        </Stack>
        <Divider sx={{ color: "text.secondary" }}>or</Divider>
        <AlternativeLoginOptions setErrors={setErrors} />
        <Stack alignItems="center" fontWeight="bold" spacing={1}>
          <Link color="otherGreen.main" href="/after-school/start-apply">
            Sign up to find childcare
          </Link>
          <Link color="otherGreen.main" href="/after-school/nanny/start-apply">
            Become a nanny
          </Link>
        </Stack>
      </Stack>
    </Container>
  );
};

EnterNumber.propTypes = {
  mobileNumber: PropTypes.string.isRequired,
  turnstileEnabled: PropTypes.bool.isRequired,
  onMobileNumberChange: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  initialErrors: PropTypes.arrayOf(PropTypes.string),
};

export default EnterNumber;
